import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Edit3, Mail, Phone, MapPin, User } from "lucide-react";
import { format } from "date-fns";
import { useLocalization } from '../providers/LocalizationProvider';

export default function CustomerTable({
  customers, onCustomerEdit, getCustomerStats, isLoading }) {
  const { t } = useTranslation();
  const { currencySymbol } = useLocalization();

  if (isLoading) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Customer</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Orders</TableHead>
            <TableHead>Total Spent</TableHead>
            <TableHead>Last Order</TableHead>
            <TableHead>{t('actions.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array(5).fill(0).map((_, i) => (
            <TableRow key={i}>
              <TableCell><Skeleton className="h-10 w-48" /></TableCell>
              <TableCell><Skeleton className="h-4 w-40" /></TableCell>
              <TableCell><Skeleton className="h-6 w-16" /></TableCell>
              <TableCell><Skeleton className="h-4 w-20" /></TableCell>
              <TableCell><Skeleton className="h-4 w-24" /></TableCell>
              <TableCell><Skeleton className="h-8 w-8" /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="elegant-card overflow-hidden">
      <Table>
        <TableHeader className="bg-slate-50">
          <TableRow>
            <TableHead className="font-bold text-slate-900">Customer</TableHead>
            <TableHead className="font-bold text-slate-900">Contact</TableHead>
            <TableHead className="font-bold text-slate-900 text-center">Orders</TableHead>
            <TableHead className="font-bold text-slate-900">Total Spent</TableHead>
            <TableHead className="font-bold text-slate-900">Last Order</TableHead>
            <TableHead className="font-bold text-slate-900 text-right">{t('actions.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                <div className="flex flex-col items-center gap-2">
                  <User className="w-12 h-12 text-slate-300" />
                  <p className="font-medium">{t('customers.table.no_data')}</p>
                  <p className="text-sm">Customers will appear here once added.</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            customers.map(customer => {
              const stats = getCustomerStats(customer);
              const initials = customer.full_name?.split(' ').map(n => n[0]).join('') || 'A';
              return (
                <TableRow key={customer.id} className="hover:bg-slate-50/50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={customer.avatar_url} />
                        <AvatarFallback className="bg-amber-100 text-amber-700 font-semibold">{initials}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold text-slate-900">{customer.full_name}</p>
                        <p className="text-xs text-slate-500">Added {format(new Date(customer.created_date), 'MMM d, yyyy')}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {customer.email && <div className="flex items-center gap-2 text-sm text-slate-600"><Mail className="w-3.5 h-3.5 text-slate-400" /> {customer.email}</div>}
                      {customer.phone && <div className="flex items-center gap-2 text-sm text-slate-600"><Phone className="w-3.5 h-3.5 text-slate-400" /> {customer.phone}</div>}
                      {customer.shipping_address?.city && <div className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {customer.shipping_address.city}</div>}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={stats.totalOrders > 0 ? "success" : "secondary"}>
                      {stats.totalOrders} order(s)
                    </Badge>
                  </TableCell>
                  <TableCell className="font-semibold text-slate-900">
                    {currencySymbol}{Number(stats.totalSpent || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {stats.lastOrderDate ? format(new Date(stats.lastOrderDate), 'MMM d, yyyy') : 'Never'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => onCustomerEdit(customer)}>
                      <Edit3 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}